import $ from 'jquery';

export class VariableProduct {
    #form;
    #$form;
    #emptyString;
    #currencySymbol;
    #stockLabels;
    #product;
    #variations;
    #attributes;
    #selectInputs;

    constructor(form) {
        this.#form = form;
        this.#$form = $(form);

        try {
            const data = JSON.parse(form.querySelector('#product-data').childNodes[0].textContent),
                { emptyString, currencySymbol, stockLabels, product, variations, attributes } = data;

            this.#emptyString = emptyString;
            this.#currencySymbol = currencySymbol;
            this.#stockLabels = stockLabels;
            this.#product = product;
            this.#variations = variations;
            this.#attributes = attributes;

            this.#selectInputs = document.querySelectorAll(
                Object.keys(this.#attributes)
                    .map((attribute) => `select[name="attribute_${attribute}"]`)
                    .join(','),
            );

            this.#init();
        } catch (e) {
            //
        }
    }

    #init() {
        this.#form.addEventListener('change', this.#onChange.bind(this));
        this.#form.addEventListener('input', this.#onInput.bind(this));
        this.#form.addEventListener('reset', this.#onReset.bind(this));

        this.#onChange();
    }

    #onInput(event) {
        if (event.target.matches('[data-spwk-add-to-cart-quantity]')) {
            const input = event.target,
                value = Number(input.value),
                min = Number(input.min),
                max = Number(input.max);

            if (isNaN(value) || isNaN(min) || isNaN(max) || min > max) {
                return;
            }

            let newValue = Math.max(1, value, min);

            if (max) {
                newValue = Math.min(newValue, max);
            }

            input.value = newValue;
        }
    }

    #onReset() {
        this.#resetSelectInputs();
        this.#reset();
    }

    #updateVariationId(id) {
        document.querySelectorAll('input[name="variation_id"]').forEach((input) => {
            input.value = id;
        });

        document.querySelectorAll('a[data-product_id]').forEach((el) => {
            el.dataset.product_id = id;
        });
    }

    #resetPrice() {
        const hasSalePrice =
            this.#product.priceRangeSale && this.#product.priceRangeSale !== this.#product.priceRangeRegular;

        document.querySelectorAll('[data-spwk-product-price]').forEach((price) => {
            price.innerHTML = this.#product.priceRange;
        });

        if (hasSalePrice) {
            document.querySelectorAll('[data-spwk-product-price-sale]').forEach((salePrice) => {
                salePrice.innerHTML = this.#product.priceRangeRegular;
            });
        }
    }

    #updateDescription(description) {
        if (description) {
            document.querySelectorAll('[data-spwk-product-description]').forEach((el) => {
                el.innerHTML = description;
            });
        }
    }

    #updateSKU(sku) {
        document.querySelectorAll('[data-spwk-product-sku]').forEach((el) => {
            el.innerHTML = sku === this.#emptyString ? '' : sku;
        });
    }

    #updateWeight(weight) {
        document.querySelectorAll('[data-spwk-product-attribute-value="attribute_weight"]').forEach((el) => {
            el.innerHTML = weight === this.#emptyString ? '' : weight;
        });
    }

    #updateDimensions(dimensions) {
        document.querySelectorAll('[data-spwk-product-attribute-value="attribute_dimensions"]').forEach((el) => {
            el.innerHTML = dimensions === this.#emptyString ? '' : dimensions;
        });
    }

    #updateAvailability(availabilityLabel) {
        document.querySelectorAll('[data-spwk-product-availability]').forEach((availability) => {
            availability.innerHTML = availabilityLabel;
        });
    }

    #resetAttributes() {
        for (let attribute of this.#product.attributes) {
            document
                .querySelectorAll(`[data-spwk-product-attribute-value='attribute_${attribute.slug}']`)
                .forEach((v) => {
                    v.innerText = attribute.value === this.#emptyString ? '' : attribute.value;
                });
        }
    }

    #updateThumbnails(thumbnail, thumbnailGallery) {
        const swipers = new Set();

        if (thumbnail) {
            document.querySelectorAll('[data-spwk-product-thumbnail-main]').forEach((container) => {
                const swiper = container.closest('.swiper')?.swiper;

                container.innerHTML = thumbnail;

                if (swiper) {
                    swipers.add(swiper);
                }
            });
        }

        if (thumbnailGallery) {
            document.querySelectorAll('[data-spwk-product-thumbnail-gallery-main]').forEach((container) => {
                container.innerHTML = thumbnailGallery;
            });
        }

        if (swipers.size) {
            for (const swiper of swipers) {
                swiper.update();
            }
        }
    }

    #updateQty(qty, min, max) {
        document.querySelectorAll('[data-spwk-add-to-cart-quantity]').forEach((input) => {
            const value = Number(input.value);

            min = Number(min);
            max = Number(max);

            if (isNaN(value) || isNaN(min) || isNaN(max)) {
                return;
            }

            if (max) {
                max = Math.max(min, max);
            } else {
                max = '';
            }

            input.setAttribute('min', min);
            input.setAttribute('max', max);

            let newValue = Math.max(1, value, min);

            if (max) {
                newValue = Math.min(newValue, max);
            }

            if (newValue !== value) {
                input.dispatchEvent(new InputEvent('input', { bubbles: true, cancelable: true }));
            }
        });
    }

    #updateAddToCartButton(disabled) {
        document.querySelector('[data-ajax-add-to-cart]').setAttribute('aria-disabled', String(disabled));
        document.querySelector('[data-simple-add-to-cart]').disabled = disabled;
    }

    #updateResetButton(hidden) {
        document.querySelectorAll('[data-spwk-product-reset]').forEach((button) => {
            button.classList.toggle('is-active', !hidden);
        });
    }

    #update(variation) {
        // update ID
        this.#updateVariationId(variation.variation_id);

        // update price
        if (variation.display_price !== variation.display_regular_price) {
            document.querySelectorAll('[data-spwk-product-price-sale]').forEach((salePrice) => {
                salePrice.innerHTML =
                    this.#currencySymbol +
                    new Intl.NumberFormat('de-DE', {
                        style: 'decimal',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(variation.display_regular_price);
            });
        }

        document.querySelectorAll('[data-spwk-product-price]').forEach((price) => {
            price.innerHTML =
                this.#currencySymbol +
                new Intl.NumberFormat('de-DE', {
                    style: 'decimal',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(variation.display_price);
        });

        this.#updateDescription(variation.variation_description);
        this.#updateSKU(variation.sku);
        this.#updateAvailability(this.#stockLabels[variation.is_in_stock ? 'instock' : 'outofstock']);
        this.#updateQty(variation.stock_qty, variation.min_qty, variation.max_qty);
        this.#updateFlags({
            isInStock: variation.is_in_stock,
            isLowStock: variation.is_low_stock,
            isRestocking: variation.is_restocking,
            isShippingGood: variation.is_shipping_good,
        });

        this.#updateWeight(variation.weight_html);
        this.#updateDimensions(variation.dimensions_html);

        // update attributes
        for (let attribute in variation.attributes) {
            if (Object.prototype.hasOwnProperty.call(variation.attributes, attribute)) {
                document.querySelectorAll(`[data-spwk-product-attribute-value='${attribute}']`).forEach((v) => {
                    v.innerText = variation.attributes[attribute].label ?? this.#emptyString;
                });
            }
        }

        this.#updateThumbnails(variation.thumbnail, variation.thumbnailGallery);
        this.#updateResetButton(false);

        this.#updateAddToCartButton(variation.is_purchasable !== true || variation.is_in_stock !== true);
    }

    #reset() {
        this.#updateVariationId(0);
        this.#resetPrice();
        this.#updateDescription(this.#product.description);
        this.#updateSKU('');
        this.#updateAvailability('');
        this.#updateQty(this.#product.stock_quantity, this.#product.min_qty, this.#product.max_qty);
        this.#updateFlags({ hideAll: true });
        this.#updateWeight('');
        this.#updateDimensions('');
        this.#resetAttributes();
        this.#updateThumbnails(this.#product.thumbnail, this.#product.thumbnailGallery);

        this.#updateResetButton(true);
        this.#updateAddToCartButton(true);

        this.#$form.trigger('reset_data');
    }

    #findVariation(attributes) {
        return this.#variations.find((variation) => {
            let found = true;

            for (let attribute in variation.attributes) {
                if (Object.prototype.hasOwnProperty.call(variation.attributes, attribute)) {
                    if (variation.attributes[attribute].value !== attributes[attribute]) {
                        found = false;
                        break;
                    }
                }
            }

            return found;
        });
    }

    #resetSelectInputs() {
        this.#selectInputs.forEach((input) => {
            const options = input.querySelectorAll('option');

            options.forEach((option) => {
                option.disabled = false;
            });
        });
    }

    #onChange(event) {
        if (event && !event.target.matches('select')) {
            return;
        }

        const formData = new FormData(this.#form),
            setValues = {},
            variation = this.#variations.find((variation) => {
                let found = true;

                for (let attribute in variation.attributes) {
                    if (Object.prototype.hasOwnProperty.call(variation.attributes, attribute)) {
                        if (variation.attributes[attribute].value !== formData.get(attribute)) {
                            found = false;
                            break;
                        }
                    }
                }

                return found;
            });

        this.#resetSelectInputs();

        for (const attribute in this.#attributes) {
            if (Object.prototype.hasOwnProperty.call(this.#attributes, attribute)) {
                const value = formData.get(`attribute_${attribute}`);

                if (value) {
                    setValues[`attribute_${attribute}`] = value;
                }
            }
        }

        if (Object.values(setValues).length) {
            const allSet = Object.values(setValues).length === this.#selectInputs.length;

            Array.from(this.#selectInputs)
                .filter((input) => {
                    return allSet || !(input.name in setValues);
                })
                .forEach((input) => {
                    const currentAttributeName = input.name;

                    input.querySelectorAll('option').forEach((option) => {
                        if (!option.value || option.selected) {
                            option.disabled = false;
                            return;
                        }

                        const hasVariants = Boolean(
                            this.#findVariation({ ...setValues, [currentAttributeName]: option.value }),
                        );

                        option.disabled = !hasVariants;
                    });
                    // }
                });
        }

        /* eslint-disable jquery/no-trigger */
        if (variation) {
            this.#update(variation);

            this.#$form.trigger('found_variation', [variation]);
            $(this.#form.querySelector('.single_variation')).trigger('show_variation', [
                variation,
                true, // = purchasable
            ]);
        } else {
            this.#reset();
        }
    }

    #updateFlags({ isInStock, isLowStock, isRestocking, isShippingGood, hideAll = false }) {
        document.querySelector('[data-spwk-product-flags]').hidden = hideAll;

        document.querySelectorAll('[data-spwk-product-flag]').forEach((flag) => {
            const flagName = flag.dataset.spwkProductFlag;

            if (hideAll) {
                flag.parentNode.hidden = true;
                return;
            }

            let isVisible = false;

            switch (flagName) {
                case 'lowstock':
                    isVisible = isInStock && isLowStock;
                    break;
                case 'instock':
                    isVisible = isInStock && !isLowStock;
                    break;
                case 'restocking':
                    isVisible = !isInStock && isRestocking;
                    break;
                case 'outofstock':
                    isVisible = !isInStock && !isRestocking;
                    break;
                case 'shippinggood':
                    isVisible = isShippingGood;
                    break;
                default:
                    break;
            }

            flag.parentNode.hidden = !isVisible;
        });
    }
}
